import { MemberTimeZoneContext } from './MemberTimeZoneContext'
import { useBookingScreenEntry } from './lib/useBookingScreenEntry'
import { RacquetSyncProvider } from './lib/useRacquetMatch'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider';
import { useModalVisualViewport } from './lib/useModalVisualViewport'
import React, { useLayoutEffect, useMemo, useRef, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { BookOpen, CalendarDays, House, LogOut, UserCircle2, UtensilsCrossed } from 'lucide-react'
import MemberCommandBar from './components/MemberCommandBar'
import NotificationBell from './components/NotificationBell'
import { buildMemberNavItems } from './lib/navigation'
import TimedMemberImage from './components/TimedMemberImage'
import { getMemberMobileLayoutMode } from './lib/mobileLayout'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function MemberLayout({
  branding,
  clubCode,
  member,
  onLogout,
  isLoggingOut,
  managedActivities = [],
  hasVotingAccess = false,
  isMobileApp = false,
  children,
}) {
  const { t: memberText } = useOptionalPrivanaI18n()

  const [desktop, setDesktop] = useState(() => window.matchMedia?.('(min-width: 961px)').matches ?? false)
  useEffect(() => {
    if (!window.matchMedia) return
    const media = window.matchMedia('(min-width: 961px)')
    const update = () => setDesktop(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  const location = useLocation()
  useBookingScreenEntry(location.pathname)
  useLayoutEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined
    const previous = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    return () => { window.history.scrollRestoration = previous }
  }, [])
  const isRacquetCheckout = isMobileApp && new RegExp(`^/${clubCode}/book-racquet/[^/]+/book/?$`).test(location.pathname)
  const shellRef = useRef(null)
  useModalVisualViewport(shellRef, isRacquetCheckout)
  const isLetsPlay = location.pathname.startsWith(`/${clubCode}/lets-play`)
  const isLetsPlayActiveRound = new RegExp(`^/${clubCode}/lets-play/(?:golf/round|(?:tennis|padel|pickleball|squash)/match)/[^/]+/?$`).test(location.pathname)

  const navItems = useMemo(
    () => buildMemberNavItems({ clubCode, managedActivities, hasVotingAccess }),
    [clubCode, managedActivities, hasVotingAccess]
  )
  const dashboardRoute = `/${clubCode}/dashboard`
  const mobileNavItems = useMemo(
    () => [
      { to: `/${clubCode}/book-facilities`, icon: BookOpen, label: "Book facilities", labelKey: "portalPresentation.member_book_facilities" },
      { to: `/${clubCode}/events`, icon: CalendarDays, label: "Events", labelKey: "membersHome.events" },
      { to: `/${clubCode}/dashboard`, icon: House, label: "Home", labelKey: "portalPresentation.member_home", isPrimary: true },
      { to: `/${clubCode}/dining`, icon: UtensilsCrossed, label: "Dining", labelKey: "portalPresentation.member_dining" },
      { to: `/${clubCode}/profile`, icon: UserCircle2, label: "Profile", labelKey: "portalPresentation.member_profile" },
    ],
    [clubCode]
  )

  const mobileLayoutMode = getMemberMobileLayoutMode(location.pathname, clubCode)
  const isEventsPage = location.pathname.replace(/\/+$/, '') === `/${clubCode}/events`
  const isProfilePage = mobileLayoutMode === 'special_profile'
  const isDiningPage = mobileLayoutMode === 'special_dining'
  const usesCanonicalMobileShell = mobileLayoutMode === 'standard'
  const isMemberResolving = Boolean(member?.isResolving)
  const memberName = member?.firstName || member?.email || memberText("portalPresentation.member_member")

  return (
    <MemberTimeZoneContext.Provider value={branding?.timeZone}><RacquetSyncProvider clubCode={clubCode}><div ref={shellRef} className={classNames('mp-app', isRacquetCheckout && 'mp-app--racquet-checkout', isMobileApp && 'mp-app--mobile', isMobileApp && isEventsPage && 'mp-app--events', isMobileApp && usesCanonicalMobileShell && !isLetsPlay && 'mp-app--mobile-content-end', isLetsPlay && 'mp-app--lets-play', isLetsPlayActiveRound && 'mp-app--lets-play-active', isProfilePage && 'mp-app--profile', isDiningPage && 'mp-app--dining')}>
      {!isMobileApp ? (
        <header className="mp-topnav">
          <NavLink
            to={dashboardRoute}
            className="mp-brand"
            aria-label={memberText("portalPresentation.member_go_to_dashboard")}
            title={memberText("portalPresentation.member_dashboard")}
          >
            {branding?.logoUrl ? (
              <TimedMemberImage className="mp-brand__logo" src={branding.logoUrl} category="club_branding" alt={branding.clubName || memberText("portalPresentation.member_club")} loading="eager" decoding="async" fallback={<span className="mp-brand__text">PRIVANA</span>} />
            ) : (
              <span className="mp-brand__text">PRIVANA</span>
            )}
          </NavLink>
          {desktop ? <MemberCommandBar navItems={navItems} clubCode={clubCode} /> : <nav className="mp-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  classNames('mp-nav__link', isActive || location.pathname.startsWith(item.to) ? 'is-active' : '')
                }
              >
                {memberText(item.labelKey)}
              </NavLink>
            ))}
          </nav>}
          <div className="mp-top-actions">
            <NotificationBell clubCode={clubCode} />
            <NavLink
              to={`/${clubCode}/profile`}
              className={({ isActive }) =>
                classNames(
                  'mp-user-chip',
                  isActive || location.pathname.startsWith(`/${clubCode}/profile`) ? 'is-active' : ''
                )
              }
            >
              <div className="mp-user-avatar">
                {member?.avatarUrl && !isMemberResolving ? (
                  <TimedMemberImage src={member.avatarUrl} category="profile_avatar" sourceType="signed_url" alt={memberText("portalPresentation.member_member_profile")} fallback={memberName?.[0]?.toUpperCase() || 'M'} />
                ) : isMemberResolving ? (
                  <span className="mp-user-avatar__placeholder" aria-hidden="true" />
                ) : (
                  memberName?.[0]?.toUpperCase() || 'M'
                )}
              </div>
              <div className="mp-user-meta">
                <div className="mp-user-name">
                  {isMemberResolving ? (
                    <span
                      className="mp-user-name__placeholder"
                      aria-label={memberText("portalPresentation.member_resolving_member_profile")}
                      role="status"
                    />
                  ) : (
                    memberName
                  )}
                </div>
                <div className="mp-user-tier">{branding?.clubName || 'Privana'}</div>
              </div>
            </NavLink>
            <button type="button" className="mp-ghost" onClick={onLogout} disabled={isLoggingOut}>
              <LogOut size={16} />
              {isLoggingOut ? memberText("dining.loggingOut") : memberText("dining.logOut")}
            </button>
          </div>
        </header>
      ) : null}
      <main className={classNames('mp-main', isMobileApp && usesCanonicalMobileShell && !isLetsPlay && 'mp-main--mobile-content-start', isMobileApp && usesCanonicalMobileShell && !isLetsPlay && 'mp-main--mobile-content-end', isProfilePage && 'mp-main--full', isProfilePage && 'mp-main--profile', isDiningPage && 'mp-main--dining')}>{children}</main>
      {isMobileApp && !isLetsPlay ? (
        <nav className="mp-mobile-bottom-nav" aria-label={memberText("portalPresentation.member_mobile_bottom_navigation")}>
          {mobileNavItems.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                aria-label={memberText(item.labelKey)}
                title={memberText(item.labelKey)}
                className={({ isActive }) =>
                  classNames(
                    'mp-mobile-bottom-nav__item',
                    item.isPrimary && 'mp-mobile-bottom-nav__item--primary',
                    isActive && 'is-active'
                  )
                }
              >
                <Icon size={item.isPrimary ? 24 : 21} />
              </NavLink>
            )
          })}
        </nav>
      ) : null}
    </div></RacquetSyncProvider></MemberTimeZoneContext.Provider>
  )
}
